import React, { useEffect, useRef, useState } from 'react';
import { Play, MessageCircle, Users } from 'lucide-react';

const YourOnlyJob = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);
  const ctaRef = useRef(null);

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'header'])]); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) headerObs.observe(sectionRef.current);

    const itemObs = itemRefs.current.map((ref, i) => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setTimeout(() => setVisibleItems(p => [...new Set([...p, i])]), i * 140);
        },
        { threshold: 0.15 }
      );
      if (ref) obs.observe(ref);
      return obs;
    });

    const ctaObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'cta'])]); },
      { threshold: 0.1 }
    );
    if (ctaRef.current) ctaObs.observe(ctaRef.current);

    return () => { headerObs.disconnect(); itemObs.forEach(o => o.disconnect()); ctaObs.disconnect(); };
  }, []);

  const responsibilities = [
    {
      icon: Play,
      title: "Keep creating content",
      description: "Do what you do best — create engaging content for your audience"
    },
    {
      icon: MessageCircle,
      title: "Communicate with your audience",
      description: "Stay connected and maintain the trust you've built"
    },
    {
      icon: Users,
      title: "Collaborate during the initial strategy phase",
      description: "Share your insights to help us understand your audience"
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-[rgb(17,17,19)] relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[rgba(197,155,47,0.06)] blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-[900px] mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${
          visibleItems.includes('header') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Your Only <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Job</span>
          </h2>
          <p className="text-xl text-[rgb(218,218,218)]">
            Your role stays intentionally light so you can keep momentum with your audience
          </p>
        </div>

        <div className="space-y-6 mb-12">
          {responsibilities.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                ref={el => (itemRefs.current[index] = el)}
                className={`flex items-start space-x-6 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:border-[rgb(197,155,47)] hover:shadow-[0_8px_30px_rgba(197,155,47,0.12)] transition-all duration-500 group ${
                  visibleItems.includes(index)
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-10 scale-95'
                }`}
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[rgba(197,155,47,0.2)] to-[rgba(124,56,178,0.2)] border-2 border-[rgb(197,155,47)] flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(197,155,47,0.4)] transition-all duration-300">
                  <Icon size={32} className="text-[rgb(197,155,47)]" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-[rgb(218,218,218)] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div ref={ctaRef} className={`text-center transition-all duration-700 ${
          visibleItems.includes('cta') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}>
          <div className="inline-block bg-gradient-to-r from-[rgba(197,155,47,0.1)] to-[rgba(124,56,178,0.1)] border border-[rgb(197,155,47)] rounded-full px-8 py-4">
            <p className="text-3xl font-bold text-[rgb(197,155,47)]">
              That's it.
            </p>
          </div>
          <p className="text-[rgb(218,218,218)] text-lg mt-6">
            We handle everything else — product development, business operations, customer support, and scaling
          </p>
        </div>
      </div>
    </section>
  );
};

export default YourOnlyJob;